import { ReactElement, useEffect, useMemo, useState } from 'react'
import useNavigation from 'core/hooks/navigation/useNavigation'
import { Navigate } from 'react-router'
import {
  DashboardUrl,
  SamlLoginUrl,
  SamlMobileAppRedirect,
  SamlPurpleMobileAppRedirect,
  SltLoginUrl,
  StartUrl
} from 'constants/RouteUrls'
import useUrlIsAppRequest from 'core/hooks/navigation/useUrlIsAppRequest'
import useAuth from 'core/hooks/auth/useAuth'
import { isSafePath } from 'core/hooks/auth/getSafeRedirectUrl'
import { hasInAppUrlSearch } from 'utils/AppHelper'
import { docCookies } from 'utils/docCookies'
import AppLoadingIndicator from 'core/widgets/app-loader/AppLoadingIndicator'
import { useSettings } from 'core/hooks/settings/useSettings'
import APIService from 'api/APIService'
import { AuthResponse, UserProfile } from 'types/user'
import useLogin from 'core/hooks/auth/useLogin'
import useNotifications from 'core/hooks/ui/useNotifications'
import { keepPreviousData } from '@tanstack/react-query'
import { useQueryCallbacks } from 'core/hooks/data/useQueryCallbacks'
import { useUserProfile } from 'core/hooks/data/useDataHook'
import {
  getFutureAccessDate,
  hasMileMarkerAccess
} from 'pages/auth/purple/helper'

/**
 * Route wrapper component that handles authentication and authorization logic.
 *
 * Provides two modes:
 * - Auth-only validation: For auth pages (login, signup) - redirects authenticated users
 * - Protected route: For app pages - redirects unauthenticated users to login
 *
 * @param children - Child components to render if validation passes
 * @param validateAuthOnly - If true, validates auth pages; if false, validates protected routes
 */
const ProtectedRoute = ({
  children,
  validateAuthOnly
}: {
  children?: ReactElement
  validateAuthOnly?: boolean
}) => {
  const {
    destroySession,
    isAuthenticated,
    isAuthLoading,
    switchSession,
    setUserId,
    setMotusTokens
  } = useAuth()
  const { pathname, search, replace } = useNavigation()
  const isAppRequest = useUrlIsAppRequest()
  const [isSwitchingSession, setIsSwitchingSession] = useState(false)
  const { handleMotusLogin, handleEverlanceLogin } = useLogin()
  const { setData: setUserData } = useUserProfile()

  // Extract query parameters
  const source = useMemo(
    () => new URLSearchParams(search).get('source'),
    [search]
  )
  const slt = new URLSearchParams(search).get('slt')
  const jwt = new URLSearchParams(search).get('jwt')
  const isMotusSsoRedirect = new URLSearchParams(search).get('motus') === 'true'

  const isSamlLandingPage = pathname === SamlLoginUrl
  const isAuthPages = pathname.includes('auth')
  const notifications = useNotifications()

  const {
    data: {
      featureFlags: { ENABLE_DRIVER_EXPERIENCE },
      auth: authSettings
    },
    set
  } = useSettings()

  const rawRedirectUrl = new URLSearchParams(search).get('redirect_url')
  // Purple tree drops unsafe redirect targets; green tree keeps legacy behavior
  const redirect_url =
    ENABLE_DRIVER_EXPERIENCE && rawRedirectUrl && !isSafePath(rawRedirectUrl)
      ? null
      : rawRedirectUrl

  useQueryCallbacks({
    queryKey: ['saml', 'motus', 'login', jwt],
    queryFn: async () => {
      const settings = await APIService.motus.getUserSettings({
        accessToken: jwt as string
      })

      const user = await APIService.motus.getCurrentUser({
        accessToken: jwt as string
      })

      const hasAccess = hasMileMarkerAccess(settings)
      const futureAccessDate = getFutureAccessDate(settings) ?? '10-10-2026'
      const isConverted =
        !!settings.convertedToNewApp || !!user?.teams?.[0]?.converted_to_new_app

      return {
        hasAccess,
        futureAccessDate,
        access_token: jwt,
        user,
        isConverted
      } as {
        hasAccess: boolean
        access_token: string
        user: UserProfile
        isConverted: boolean
        futureAccessDate?: string
      }
    },
    enabled: !!jwt && isSamlLandingPage && isMotusSsoRedirect,
    placeholderData: keepPreviousData,
    refetchOnMount: false,
    onSuccess: ({
      hasAccess,
      access_token,
      user,
      isConverted,
      futureAccessDate
    }) => {
      if (hasAccess && user) {
        setMotusTokens({
          refreshToken: null,
          accessToken: access_token
        })
        setUserData(user)

        handleMotusLogin({
          isConverted,
          user
        })
        return
      }

      setTimeout(() => {
        set('auth', {
          ...authSettings,
          isAuthenticated: false,
          isAuthLoading: false
        })
      }, 300)

      if (futureAccessDate) {
        replace(
          StartUrl.addUrlParams({
            futureAccessDate: futureAccessDate as string
          })
        )
        return
      }

      notifications.error({
        title: 'SSO authentication failed.',
        subtitle: 'Please try logging in again.'
      })
    },
    onError: () => {
      notifications.error({
        title: 'SSO authentication failed.',
        subtitle: 'Please try logging in again.'
      })
      replace(StartUrl)
      setTimeout(() => {
        set('auth', {
          ...authSettings,
          isAuthenticated: false,
          isAuthLoading: false
        })
      }, 300)
    }
  })

  useQueryCallbacks({
    queryKey: ['saml', 'login', jwt],
    queryFn: () => {
      return APIService.everlance.switchSessions({
        authToken: jwt as string
      })
    },
    enabled: !!jwt && isSamlLandingPage && !isMotusSsoRedirect,
    placeholderData: keepPreviousData,
    refetchOnMount: false,
    onSuccess: (data) => {
      const authResponse: AuthResponse = {
        ...data,
        auth_token: jwt as string
      }
      setUserId(data.token_id as string)
      setUserData(data)
      handleEverlanceLogin({
        user: authResponse
      })
    },
    onError: () => {
      notifications.error({
        title: 'SSO authentication failed.',
        subtitle: 'Please try logging in again.'
      })
      replace(StartUrl)
      setTimeout(() => {
        set('auth', {
          ...authSettings,
          isAuthenticated: false,
          isAuthLoading: false
        })
      }, 300)
    }
  })
  /**
   * Hides Ada chatbot for mobile app requests.
   */
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isAppRequest && !document.getElementById('is-app-request')) {
        const styleTag = document.createElement('style')
        document.head.appendChild(styleTag)
        styleTag.setAttribute('id', 'is-app-request')
        styleTag.innerText = '#ada-button-frame {display: none !important;}'
      }
    }, 300)
    return () => clearTimeout(timeout)
  }, [isAppRequest])

  // ========================================
  // Auth-Only Validation (Login/Signup/SSO Pages)
  // ========================================
  if (validateAuthOnly) {
    /**
     * Mobile app SAML authentication redirect
     * - When: Mobile app request with JWT token present
     * - Action: Destroy session, hide chat, redirect to mobile app with JWT
     * - Why: Mobile app needs JWT delivered via deep link, not web session
     */
    if (isAppRequest && jwt) {
      destroySession()
      document.querySelector('#ada-chat-button')?.classList.add('!hidden')

      // When flag enabled it means we're opening the dashboard inside the purple app
      const appUrl =
        ENABLE_DRIVER_EXPERIENCE || source === 'reimburse'
          ? SamlPurpleMobileAppRedirect
          : SamlMobileAppRedirect

      window.location.href = appUrl.replaceParams({
        jwt
      })
      return null
    }

    /**
     * Already authenticated user on auth pages
     * - When: User is authenticated but trying to access auth pages (login/signup)
     * - Action: Redirect to dashboard or specified redirect URL - and not being on the saml_callback url
     * - Why: Logged-in users shouldn't see login/signup pages
     */
    if (
      isAuthPages &&
      !isSamlLandingPage &&
      isAuthenticated &&
      !isAuthLoading
    ) {
      return (
        <Navigate to={redirect_url ? redirect_url : DashboardUrl} replace />
      )
    }

    /**
     * SAML authentication in progress
     * - When: On SAML landing page with JWT, authentication not yet complete
     * - Action: Show loading indicator while SAML login query executes
     * - Why: Provide visual feedback during authentication process
     */
    if (isSamlLandingPage && !isAuthenticated && jwt) {
      return <AppLoadingIndicator />
    }

    // Auth pages are accessible to unauthenticated users
    return <>{children}</>
  }

  // ========================================
  // Protected Route Validation (App Pages)
  // ========================================

  /**
   * Authenticated user with redirect URL
   * - When: User is authenticated and a redirect_url is specified
   * - Action: Navigate to the specified redirect URL
   * - Why: Complete post-login redirect flow (e.g., from email links)
   */
  if (isAuthenticated && redirect_url) {
    return <Navigate to={redirect_url} replace />
  }

  /**
   * Unauthenticated user with SLT (Single Login Token)
   * - When: User is not authenticated but has an SLT token
   * - Action: Redirect to SLT login page with token
   * - Why: SLT allows secure one-time login without credentials
   */
  if (!isAuthenticated && slt) {
    return (
      <Navigate
        to={`${SltLoginUrl}?slt=${slt}&redirect_url=${pathname}`}
        replace
      />
    )
  }

  /**
   * Unauthenticated user accessing protected route
   * - When: User is not authenticated
   * - Action: Redirect to login with current path as redirect_url
   * - Why: Preserve intended destination after successful login
   *
   * Special case: Hash-based routing
   * - Handles legacy hash routes (e.g., #/some-path)
   * - Converts to query string if hash matches start URL
   */
  if (!isAuthenticated) {
    if (hasInAppUrlSearch(search)) {
      /**
       * We are assuming that when inAppView param is set then it's a request coming from mobile webview component.
       * Mobile apps set the cookie "auth_token" before opening the webview, so we can use it to create a session
       */
      const authToken = docCookies.getItem('auth_token')
      if (authToken && !isSwitchingSession) {
        setIsSwitchingSession(true)
        switchSession(authToken)
      }
      return <AppLoadingIndicator />
    }

    const withRedirect = new URLSearchParams(search)
    withRedirect.set('redirect_url', pathname)

    // Handle legacy hash-based routing
    if (window.location.hash) {
      const hashPath = window.location.hash.slice(1)
      const [hashPathWithoutParams = '', urlQueryString = ''] =
        hashPath.split('?')
      if (StartUrl.endsWith(hashPathWithoutParams)) {
        return <Navigate to={`${StartUrl}?${urlQueryString}`} replace />
      }
    }

    return <Navigate to={`${StartUrl}?${withRedirect.toString()}`} replace />
  }

  // User is authenticated and passed all validations - render protected content
  return <>{children}</>
}

export default ProtectedRoute
